import { isEqual, pick } from 'lodash';
import {
  APICurrentUser,
  OrgAccessLevel,
  PersonalSetting,
} from '@ardoq/api-types';
import { userOperations } from '@ardoq/core';
import { permissionsOperations } from '@ardoq/access-control';
import { addDays } from '@ardoq/date-time';
import { CarouselConfigurableProps } from './carousel/Carousel';
import {
  EphemeralBannerConfig,
  EphemeralNotificationConfig,
  EphemeralNotificationKeys,
  EphemeralNotificationRelevanceContext,
} from './types';
import {
  EphemeralNotificationRecords,
  ephemeralNotificationsStateOperations,
} from './ephemeralNotificationsStateOperations';

const CLEANUP_JOB_BACKSTOP_DAYS = 14;

export const canAccessEphemeralNotificationsDevTool = (
  currentUser: APICurrentUser
): boolean =>
  userOperations.isArdoqEmployee(currentUser) &&
  permissionsOperations.hasRequiredOrgAccessLevel(
    OrgAccessLevel.ADMIN,
    currentUser
  );

/**
 * Whether any interaction may resolve the notification. A config can opt out
 * with `isResolvable`.
 */
export const isNotificationResolvable = (
  notificationConfig: EphemeralNotificationConfig
): boolean =>
  notificationConfig.kind === 'banner' ||
  notificationConfig.kind === 'heroBanner'
    ? (notificationConfig.isResolvable?.() ?? true)
    : true;

/**
 * When the expiry warning fires: 14 days after the last date the cleanup job
 * waits for, so it only fires once that job has stopped running. `undefined`
 * for a no-expiry notification, which the cleanup job never waits on.
 */
export const getExpiryWarningDate = (
  notificationConfig: EphemeralNotificationConfig
): Date | undefined =>
  notificationConfig.relevantUntilDate === undefined
    ? undefined
    : addDays(notificationConfig.relevantUntilDate, CLEANUP_JOB_BACKSTOP_DAYS);

const carouselFeaturesConfigurablePropKeys: Record<
  keyof CarouselConfigurableProps,
  true
> = {
  cards: true,
  size: true,
  endButtonLabel: true,
  withShadow: true,
  nextButtonLabel: true,
  backButtonLabel: true,
  closeButtonLabel: true,
  showCloseButton: true,
  showSkipButton: true,
  skipButtonLabel: true,
};

export const toCarouselConfigurableProps = (
  config: CarouselConfigurableProps
): CarouselConfigurableProps =>
  pick(
    config,
    Object.keys(
      carouselFeaturesConfigurablePropKeys
    ) as (keyof CarouselConfigurableProps)[]
  );

/** Legacy ledger, will be removed once EPHEMERAL_NOTIFICATIONS_STATE is the only source of truth. */
export const getEphemeralNotificationDismissedKeys = (
  currentUserState: APICurrentUser
) =>
  currentUserState.settings[
    PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS
  ];

/** Records merged with the legacy ledger for keys the new format hasn't recorded yet. */
export const getEphemeralNotificationRecords = (
  currentUserState: APICurrentUser,
  now: Date
): EphemeralNotificationRecords => {
  const state =
    currentUserState.settings[PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE];
  const legacyDismissedKeys =
    getEphemeralNotificationDismissedKeys(currentUserState) ?? [];

  return legacyDismissedKeys.reduce(
    (records, notificationKey) =>
      ephemeralNotificationsStateOperations.withLegacyDismissal(
        records,
        notificationKey,
        now
      ),
    state?.records ?? {}
  );
};

/** The pruned merged view, or `null` if it already matches the raw stored records. */
export const getRecordsToMigrate = (
  currentUserState: APICurrentUser,
  now: Date
): EphemeralNotificationRecords | null => {
  const storedRecords =
    currentUserState.settings[PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE]
      ?.records ?? {};
  const mergedRecords = ephemeralNotificationsStateOperations.pruneRecords(
    getEphemeralNotificationRecords(currentUserState, now)
  );

  return isEqual(mergedRecords, storedRecords) ? null : mergedRecords;
};

/** Records as they were when the session started. Only `dependsOnResolved` and the view cap read this; exported so tests can reset it. */
let sessionRecordsSnapshot: EphemeralNotificationRecords | null = null;

export const getSessionRecordsSnapshot = (
  currentUserState: APICurrentUser,
  now: Date
): EphemeralNotificationRecords => {
  sessionRecordsSnapshot ??= getEphemeralNotificationRecords(
    currentUserState,
    now
  );
  return sessionRecordsSnapshot;
};

export const resetSessionRecordsSnapshot = (): void => {
  sessionRecordsSnapshot = null;
};

/**
 * Whether a resolving interaction may go through right now — distinct from
 * `isNotificationResolvable`, which asks whether it may ever be resolved.
 * Reads live records, since the session snapshot isn't seeded for a
 * `resolvableAfter`-only config.
 */
export const isNotificationResolvableNow = (
  notificationConfig: EphemeralBannerConfig,
  currentUserState: APICurrentUser,
  now: Date
): boolean =>
  isNotificationResolvable(notificationConfig) &&
  ephemeralNotificationsStateOperations.isResolveDelayElapsed({
    resolvableAfter: notificationConfig.resolvableAfter,
    record: getEphemeralNotificationRecords(currentUserState, now)[
      notificationConfig.id
    ],
    now,
  });

/** Whether the notification was resolved before the current session started. */
const wasResolvedInPreviousSession = (
  key: EphemeralNotificationKeys,
  currentUser: APICurrentUser,
  now: Date
): boolean =>
  ephemeralNotificationsStateOperations.isNotificationResolved(
    getSessionRecordsSnapshot(currentUser, now),
    key
  );

/** Reads live records — freezing this would mean a personal window never closes for the rest of a long-lived session. */
const isWithinPersonalWindowForUser = (
  notificationConfig: EphemeralNotificationConfig,
  currentUserState: APICurrentUser,
  now: Date
): boolean => {
  const { personalWindowDays } = notificationConfig;
  if (personalWindowDays === undefined) return true;

  return ephemeralNotificationsStateOperations.isWithinPersonalWindow({
    record: getEphemeralNotificationRecords(currentUserState, now)[
      notificationConfig.id
    ],
    personalWindowDays,
    now,
  });
};

const isWithinViewCapForUser = (
  notificationConfig: EphemeralNotificationConfig,
  currentUserState: APICurrentUser,
  now: Date
): boolean => {
  const { maxViews } = notificationConfig;
  if (maxViews === undefined) return true;

  return ephemeralNotificationsStateOperations.isWithinViewCap({
    record: getSessionRecordsSnapshot(currentUserState, now)[
      notificationConfig.id
    ],
    maxViews,
  });
};

const isNotificationResolvedForUser = (
  notificationKey: EphemeralNotificationKeys,
  currentUserState: APICurrentUser,
  now: Date
): boolean =>
  ephemeralNotificationsStateOperations.isNotificationResolved(
    getEphemeralNotificationRecords(currentUserState, now),
    notificationKey
  );

type IsDependencySatisfiedArgs = {
  dependencyConfig: EphemeralNotificationConfig;
  currentUser: APICurrentUser;
  relevanceContext: EphemeralNotificationRelevanceContext;
  now: Date;
};

/**
 * A dependency that cannot be resolved must not gate the dependent
 * notification, which would otherwise stay hidden for the rest of its window.
 * `personalWindowDays`/`maxViews` can make a dependency permanently
 * unresolvable too — its record stays `seen` forever once the window or view
 * cap is hit without an interaction — so they get the same escape hatch.
 */
export const isDependencySatisfied = ({
  dependencyConfig,
  currentUser,
  relevanceContext,
  now,
}: IsDependencySatisfiedArgs): boolean => {
  if (
    dependencyConfig.relevantUntilDate !== undefined &&
    now > dependencyConfig.relevantUntilDate
  )
    return true;
  if (!dependencyConfig.isRelevantForCurrentUser(currentUser, relevanceContext))
    return true;
  if (!isNotificationResolvable(dependencyConfig)) return true;
  if (!isWithinPersonalWindowForUser(dependencyConfig, currentUser, now))
    return true;
  if (!isWithinViewCapForUser(dependencyConfig, currentUser, now)) return true;
  return wasResolvedInPreviousSession(dependencyConfig.id, currentUser, now);
};

type IsEphemeralNotificationVisibleArgs = {
  notificationConfig: EphemeralNotificationConfig;
  currentUserState: APICurrentUser;
  relevanceContext: EphemeralNotificationRelevanceContext;
  now: Date;
};
export const isEphemeralNotificationVisible = ({
  notificationConfig,
  currentUserState,
  relevanceContext,
  now,
}: IsEphemeralNotificationVisibleArgs) => {
  const { relevantFromDate, relevantUntilDate, isRelevantForCurrentUser } =
    notificationConfig;

  return (
    // Central gate: hide every ephemeral notification while impersonating.
    !userOperations.isImpersonating(currentUserState) &&
    now >= relevantFromDate &&
    (relevantUntilDate === undefined || now <= relevantUntilDate) &&
    isRelevantForCurrentUser(currentUserState, relevanceContext) &&
    isWithinPersonalWindowForUser(notificationConfig, currentUserState, now) &&
    isWithinViewCapForUser(notificationConfig, currentUserState, now) &&
    !isNotificationResolvedForUser(notificationConfig.id, currentUserState, now)
  );
};
